import React, { useState } from 'react';
import { Box, Button, ButtonBase, Menu, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog, InlineMessage } from '../../../components/ui';
import { tokens } from '../../../theme/tokens';
import { commandDashboard } from '../commandDashboardTokens';
import type { DashboardLayoutController } from '../useDashboardLayout';

const { surface, line, ink, accent, radius, shadow } = tokens;

/** The bar sits on the dark header surface, so it uses the dashboard's on-navy tokens. */
const onDark = { line: commandDashboard.color.onNavyButtonBorder, hover: commandDashboard.color.onNavyButtonHover, hint: ink.faint };

const PlusIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const DotsIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <circle cx="5" cy="12" r="1.8" />
    <circle cx="12" cy="12" r="1.8" />
    <circle cx="19" cy="12" r="1.8" />
  </svg>
);

const LayoutIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" stroke={ink.bodyAlt} strokeWidth="1.7" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" stroke={ink.bodyAlt} strokeWidth="1.7" />
    <rect x="3.5" y="13.5" width="17" height="7" rx="1.5" stroke={ink.bodyAlt} strokeWidth="1.7" />
  </svg>
);

const darkOutline = {
  height: 34,
  border: `1px solid ${onDark.line}`,
  borderRadius: `${radius.control}px`,
  color: ink.onDark,
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
  '&:hover': { bgcolor: onDark.hover },
} as const;

interface DashboardEditBarProps {
  controller: DashboardLayoutController;
}

/**
 * "Customize" button while viewing; the dark EditTopBar while editing (title and
 * hint, Add widget with the hidden count, a more menu, Cancel, Save layout). The
 * toast for hide / add / reset / save lives here because it outlasts edit mode.
 */
export const DashboardEditBar: React.FC<DashboardEditBarProps> = ({ controller }) => {
  const { t } = useTranslation();
  const tt = t as unknown as (key: string) => string;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  if (!controller.enabled) return null;

  const toast = controller.toast && (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        left: { xs: 16, md: 256 },
        bottom: 16,
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: surface.header,
        borderRadius: `${radius.card}px`,
        boxShadow: shadow.flyout,
        py: 1,
        pr: 1,
        pl: 1.75,
      }}
    >
      <Typography sx={{ fontSize: 13, color: ink.onDarkBright }}>{controller.toast.message}</Typography>
      {controller.toast.undo && (
        <ButtonBase
          onClick={controller.toast.undo}
          sx={{ height: 30, px: 1.25, borderRadius: `${radius.small}px`, fontSize: 13, fontWeight: 600, color: tokens.signal.info.solid, '&:hover': { bgcolor: onDark.hover } }}
        >
          {tt('dashboardLayout.undo')}
        </ButtonBase>
      )}
    </Box>
  );

  if (!controller.editing) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, px: 3, pt: 1 }}>
          {controller.loadError && <InlineMessage severity="error">{controller.loadError.message}</InlineMessage>}
          <ButtonBase
            disabled={!controller.loaded}
            onClick={controller.startEditing}
            sx={{
              height: 34,
              px: 1.5,
              gap: '7px',
              border: `1px solid ${line.default}`,
              bgcolor: surface.card,
              borderRadius: `${radius.control}px`,
              fontSize: 13,
              fontWeight: 600,
              color: ink.body,
              '&:hover': { bgcolor: surface.sunken, borderColor: line.strong },
              '&.Mui-disabled': { opacity: 0.5 },
            }}
          >
            <LayoutIcon />
            {tt('dashboardLayout.customize')}
          </ButtonBase>
        </Box>
        {toast}
      </>
    );
  }

  const cancel = () => (controller.isDirty ? setConfirmDiscard(true) : controller.cancel());
  const hiddenCount = controller.addable.length;

  return (
    <>
      <Box
        sx={{
          height: 56,
          position: 'sticky',
          top: 0,
          zIndex: 20,
          bgcolor: surface.header,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          pl: '20px',
          pr: '12px',
        }}
      >
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: ink.onDarkBright, whiteSpace: 'nowrap', flex: '0 0 auto' }}>
          {tt('dashboardLayout.editing')}
        </Typography>
        <Typography
          sx={{
            display: { xs: 'none', lg: 'block' },
            fontSize: 12.5,
            color: onDark.hint,
            flex: '1 1 0',
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {tt('dashboardLayout.editHint')}
        </Typography>
        <Box sx={{ flex: '1 1 0', display: { xs: 'block', lg: 'none' } }} />

        <ButtonBase
          onClick={controller.toggleDrawer}
          aria-expanded={controller.drawerOpen}
          sx={{ ...darkOutline, gap: '6px', px: 1.5, fontSize: 13, fontWeight: 600 }}
        >
          <PlusIcon />
          {tt('dashboardLayout.addWidget')}
          {hiddenCount > 0 && (
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: surface.header, bgcolor: ink.onDark, borderRadius: '8px', px: '6px', py: '1px' }}>
              {hiddenCount}
            </Box>
          )}
        </ButtonBase>

        <ButtonBase
          aria-label={tt('dashboardLayout.moreOptions')}
          aria-haspopup="menu"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ ...darkOutline, width: 34 }}
        >
          <DotsIcon />
        </ButtonBase>
        <Menu
          anchorEl={anchor}
          open={!!anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: { width: 260, mt: 0.75, p: '6px', border: `1px solid ${line.default}`, borderRadius: `${radius.card}px`, boxShadow: shadow.menu },
            },
            list: { sx: { p: 0 } },
          }}
        >
          {[
            { key: 'reset', title: tt('dashboardLayout.reset'), sub: tt('dashboardLayout.resetSub'), run: controller.reset },
            { key: 'org', title: tt('dashboardLayout.saveOrgDefault'), sub: tt('dashboardLayout.saveOrgDefaultSub'), run: controller.saveAsOrganizationDefault },
          ].map((item) => (
            <ButtonBase
              key={item.key}
              disabled={controller.saving}
              onClick={() => {
                setAnchor(null);
                item.run();
              }}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                px: '10px',
                py: '9px',
                borderRadius: `${radius.small}px`,
                textAlign: 'left',
                '&:hover': { bgcolor: surface.sunken },
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: ink.primary }}>{item.title}</Typography>
              <Typography sx={{ fontSize: 12, color: ink.muted }}>{item.sub}</Typography>
            </ButtonBase>
          ))}
        </Menu>

        <Box sx={{ width: '1px', height: 22, bgcolor: onDark.line, mx: '4px', flex: '0 0 auto' }} />
        <Button
          disabled={controller.saving}
          onClick={cancel}
          sx={{ height: 34, px: 1.5, flex: '0 0 auto', textTransform: 'none', fontSize: 13, fontWeight: 600, color: ink.disabled, borderRadius: `${radius.control}px`, '&:hover': { bgcolor: onDark.hover, color: ink.inverse } }}
        >
          {tt('dashboardLayout.cancel')}
        </Button>
        <Button
          disabled={controller.saving}
          onClick={controller.save}
          sx={{ height: 34, px: 2, flex: '0 0 auto', textTransform: 'none', fontSize: 13, fontWeight: 600, color: ink.inverse, bgcolor: accent.main, borderRadius: `${radius.control}px`, boxShadow: 'none', '&:hover': { bgcolor: accent.hover, boxShadow: 'none' } }}
        >
          {tt('dashboardLayout.save')}
        </Button>
      </Box>
      {controller.error && (
        <Box sx={{ px: 3 }}>
          <InlineMessage severity="error">{controller.error.message}</InlineMessage>
        </Box>
      )}
      <ConfirmDialog
        open={confirmDiscard}
        title={tt('dashboardLayout.discard.title')}
        message={tt('dashboardLayout.discard.body')}
        confirmLabel={tt('dashboardLayout.discard.confirm')}
        cancelLabel={tt('dashboardLayout.discard.keep')}
        confirmColor="error"
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          controller.cancel();
        }}
      />
      {toast}
    </>
  );
};
